// front/src/admin/AdminOrderDetails.jsx
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchOrder, updateOrder } from '../../api/admin/ordersApi';
import { labelOrderStatus, nextOrderStatuses } from '../../utils/orderStatus';

import {
  createInvoiceFromOrder,
  createSalesActFromOrder,
  createOrderPdfFromOrder,
  listOrderDocs,
  toAbsoluteUrl,
} from '../../api/admin/docs.api';
import SellerProfileSelect from '../../components/SellerProfileSelect';

export default function AdminOrderDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [issuedDocs, setIssuedDocs] = useState([]);
  const [docLoading, setDocLoading] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');
  const [sellerProfileId, setSellerProfileId] = useState('');

  async function loadDocs() {
    try {
      const items = await listOrderDocs(id);
      setIssuedDocs(items);
    } catch {
      setIssuedDocs([]);
    }
  }

  async function load() {
    setLoading(true);
    try {
      const data = await fetchOrder(id);
      setOrder(data);
      setNoteDraft(data?.note || '');
      await loadDocs();
    } catch (e) {
      console.error(e);
      alert('Заказ не найден');
      navigate('/admin/orders');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading && !order) return <p>Загрузка...</p>;
  if (!order) return <p>Загрузка...</p>;

  const next = order.allowedNextStatuses || nextOrderStatuses(order.status);

  const handleGenerateDocs = async (type, { force = false } = {}) => {
    setDocLoading(true);
    const opts = { sellerProfileId: sellerProfileId || undefined, force };
    try {
      let res;
      if (type === 'invoice') res = await createInvoiceFromOrder(id, opts);
      else if (type === 'order') res = await createOrderPdfFromOrder(id, opts);
      else if (type === 'act') res = await createSalesActFromOrder(id, opts);
      if (res?.pdfUrl) {
        const abs = toAbsoluteUrl(res.pdfUrl);
        window.open(abs, '_blank', 'noopener,noreferrer');
        if (res.reused) {
          alert('Документ уже был выдан — открыт существующий. Для новой версии нажмите «Перевыпустить».');
        }
      }
      await loadDocs();
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error || e?.response?.data?.message || 'Ошибка при формировании документа');
    } finally {
      setDocLoading(false);
    }
  };

  const changeStatus = async (status) => {
    const isCancelPaid = status === 'cancelled' && ['paid', 'assembling', 'shipped'].includes(order.status);
    const isMarkPaid = status === 'paid' && ['reserved', 'new'].includes(order.status);
    let msg = `Сменить статус на «${labelOrderStatus(status)}»?`;
    if (isCancelPaid) {
      msg = 'Отменить оплаченный заказ? Склад будет возвращён. Возврат денег в T‑Bank — вручную.';
    } else if (isMarkPaid) {
      msg = 'Отметить оплаченным вручную? Будет списан склад и создана продажа (Sale).';
    } else if (status === 'cancelled' && order.status === 'reserved') {
      msg = 'Отменить заказ в резерве? Резерв снимется, бонусы вернутся (если оплата ещё не pending).';
    }
    if (!window.confirm(msg)) return;
    setSaving(true);
    try {
      const updated = await updateOrder(id, { status });
      setOrder(updated);
    } catch (e) {
      alert(e?.response?.data?.message || e?.message || 'Не удалось сменить статус');
    } finally {
      setSaving(false);
    }
  };

  const saveNote = async () => {
    setSaving(true);
    try {
      const updated = await updateOrder(id, { note: noteDraft });
      setOrder(updated);
      alert('Заметка сохранена');
    } catch (e) {
      alert(e?.response?.data?.message || e?.message || 'Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <h2 className="admin-page-title">Заказ {order.number}</h2>
      <p>
        Источник:{' '}
        {order.source === 'website' ? 'С сайта' : 'Касса'}
        {order.saleId ? ' · есть Sale' : ''}
      </p>
      <p>
        Статус:{' '}
        <span className={`admin-badge admin-badge--${order.status}`}>
          {labelOrderStatus(order.status)}
        </span>
      </p>
      {order.needsAttention && (
        <p style={{ color: '#b45309' }}>
          <b>Требует внимания:</b> {order.note || 'проверьте склад/оплату'}
          {' '}
          <button
            disabled={saving}
            onClick={async () => {
              setSaving(true);
              try {
                const updated = await updateOrder(id, { needsAttention: false });
                setOrder(updated);
              } catch (e) {
                alert(e?.response?.data?.message || 'Не удалось снять флаг');
              } finally {
                setSaving(false);
              }
            }}
          >
            Снять флаг
          </button>
        </p>
      )}
      <p>Оплата: {order.payment?.status || '—'}{order.payment?.paidAt ? ` · ${new Date(order.payment.paidAt).toLocaleString('ru-RU')}` : ''}</p>
      <p>
        Получение:{' '}
        {order.fulfillment?.method === 'pickup' ? 'Самовывоз' : 'Доставка'}
        {order.fulfillment?.comment ? ` · ${order.fulfillment.comment}` : ''}
      </p>
      <p>
        Памятка о возврате:{' '}
        {order.returnMemoProvidedAt
          ? `выдана ${new Date(order.returnMemoProvidedAt).toLocaleString('ru-RU')}${order.returnMemoMethod ? ` (${order.returnMemoMethod})` : ''}`
          : 'ещё не отмечена'}
        {' '}
        {!order.returnMemoProvidedAt && (
          <button
            type="button"
            disabled={saving}
            onClick={async () => {
              setSaving(true);
              try {
                const updated = await updateOrder(id, { returnMemoProvided: true, returnMemoMethod: 'printed' });
                setOrder(updated);
              } catch (e) {
                alert(e?.response?.data?.message || 'Не удалось отметить памятку');
              } finally {
                setSaving(false);
              }
            }}
          >
            Отметить выдачу (печатная)
          </button>
        )}
        {' · '}
        <a href="/return-memo" target="_blank" rel="noreferrer">открыть памятку</a>
      </p>
      <p>Сумма: <b>{Number(order.total || 0).toLocaleString('ru-RU')} ₽</b>
        {Number(order.bonusRedeemed || 0) > 0 ? ` (бонусы −${Number(order.bonusRedeemed).toLocaleString('ru-RU')} ₽)` : ''}
      </p>

      {next.length > 0 && (
        <div className="buttons-row" style={{ margin: '12px 0', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {next.map((st) => (
            <button key={st} disabled={saving} onClick={() => changeStatus(st)}>
              → {labelOrderStatus(st)}
            </button>
          ))}
        </div>
      )}

      <h3>Покупатель</h3>
      <p>{order.buyer?.name}</p>
      <p>{order.buyer?.phone}</p>
      <p>{order.buyer?.email}</p>
      <p>{order.buyer?.address}</p>

      <h3>Заметка</h3>
      <textarea
        value={noteDraft}
        onChange={(e) => setNoteDraft(e.target.value)}
        rows={3}
        style={{ width: '100%', maxWidth: 560 }}
      />
      <div style={{ marginTop: 8 }}>
        <button disabled={saving} onClick={saveNote}>Сохранить заметку</button>
      </div>

      <h3>Товары</h3>
      <table className="admin-table">
        <thead>
          <tr>
            <th>Артикул</th>
            <th>Наименование</th>
            <th>Кол-во</th>
            <th>Цена</th>
            <th>Сумма</th>
          </tr>
        </thead>
        <tbody>
          {order.items.map(i => (
            <tr key={i._id || `${i.productId}-${i.sku}`}>
              <td>{i.sku}</td>
              <td>{i.title}</td>
              <td>{i.qty}</td>
              <td>{i.price}</td>
              <td>{(i.qty * i.price).toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3>Документы</h3>
      {issuedDocs.length > 0 && (
        <ul style={{ marginBottom: 12 }}>
          {issuedDocs.map((d) => (
            <li key={d._id}>
              {d.title}{' '}
              <a href={toAbsoluteUrl(d.filePath)} target="_blank" rel="noreferrer">открыть</a>
              {d.version > 1 ? ` · v${d.version}` : ''}
            </li>
          ))}
        </ul>
      )}
      <div style={{ marginBottom: 10 }}>
        <SellerProfileSelect value={sellerProfileId} onChange={setSellerProfileId} />
      </div>
      <div className="buttons-row" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <button disabled={docLoading} onClick={() => handleGenerateDocs('invoice')}>
          {docLoading ? '…' : 'Счёт'}
        </button>
        <button disabled={docLoading} onClick={() => handleGenerateDocs('invoice', { force: true })}>
          Перевыпустить счёт
        </button>
        <button disabled={docLoading} onClick={() => handleGenerateDocs('order')}>
          Заказ PDF
        </button>
        <button disabled={docLoading} onClick={() => handleGenerateDocs('act')}>
          Реализация
        </button>
        <button disabled={docLoading} onClick={() => handleGenerateDocs('act', { force: true })}>
          Перевыпустить реализацию
        </button>
      </div>
      <p className="hint" style={{ marginTop: 8 }}>
        Повторное нажатие открывает уже выданный документ. «Перевыпустить» создаёт новую версию.
      </p>
    </div>
  );
}
